import {
  FaceRecognitionStatusEnum,
  BroadcastAsset,
  MediaContentResponse,
  AssetRating,
  SavedSearches,
  LayoutEnum,
  ThumbnailDetails
} from '@goldcast/api/content';
import { CSSProperties } from 'react';
import { Word } from './transcript';
import { DeletedRange, EditedWord } from '@/stores/clip';
import { SpeakerWithDetails } from '@/context/TranscriptContext/TranscriptContextTypes';
import { RemotionConfig } from '@/App/remotion/types';
import { CompositionConfig } from '@/App/remotion/MainPlayer/types/CompositionConfig';
import { LanguageOption } from '@/Pages/Clip/CaptionTranslation/types';
import { Scenes } from '@/App/remotion/MainPlayer/types/Scene';
import { CaptionPresetName } from '@/App/remotion/libs/caption/caption-presets/types';
import { CaptionTransitions, SpeakerLabelTransitions } from '@/App/remotion/libs/transitions/types';
import { FontSizePreset } from '@/libs/speakerLabelFontSize';

// TODO: Temporary solution till we find a way to export it from BE
export type SizeType = 'LANDSCAPE' | 'PORTRAIT' | 'SQUARE';
export type LayoutType = LayoutEnum;
export type SourceType = 'AI' | 'USER';
export type TextType = 'BLOG' | 'CUSTOM' | 'EMAIL' | 'LINKEDIN' | 'TAKEAWAYS';
export type HighlightType = 'none' | 'text' | 'box';
export type FitType = 'FIT' | 'FILL';
export type DeleteType = 'FILLER_WORDS_MENU' | 'MANUAL' | 'AI_EDITS';

export interface LayoutStatus {
  status: FaceRecognitionStatusEnum;
  reason: string;
  accurate: boolean;
}

export interface CaptionsPosition {
  x: number;
  y: number;
  width: number;
  height: number;
  textX?: number;
  textY?: number;
}

export enum CaptionsPlacement {
  Top = 'Top',
  Middle = 'Middle',
  Bottom = 'Bottom'
}

export enum HighlightTypeEnum {
  None = 'none',
  Text = 'text',
  Box = 'box'
}

export interface CaptionStyles {
  caps: boolean;
  placement?: CaptionsPlacement;
  font_size: number;
  line_config: CaptionLineConfig;

  /**
   * @deprecated
   */
  scale: number;

  /**
   * @deprecated
   */
  animation: CaptionStyle;

  /**
   * @deprecated
   */
  background_style_color?: string;

  font_location?: string;
  theme_preset?: CaptionPresetName | 'custom';
  pageStyle?: Omit<CSSProperties, 'backgroundColor'> & {
    /**
     * @deprecated Using 'backgroundColor' in pageStyle is discouraged. Use 'background' instead.
     */
    backgroundColor?: never;
  };
  wordStyle?: {
    active?: CSSProperties;
    inactive?: CSSProperties;
    highlightType?: HighlightTypeEnum;
    highlightBoxStyle?: CSSProperties;
  };
  letterStyle?: CSSProperties;
  transitions?: CaptionTransitions;
}

export interface SpeakerLabelTextStyle extends CSSProperties {
  fontSizePreset?: FontSizePreset;
}

export interface SpeakerLabelStyles {
  font_location: string;
  title_font_location?: string;
  text_color: string;
  background_color: string;
  border_radius: string;
  containerStyle?: CSSProperties;
  nameStyle?: SpeakerLabelTextStyle;
  titleStyle?: SpeakerLabelTextStyle;
  transitions?: SpeakerLabelTransitions;
}

export interface ScreenshareDetectionStatus {
  status: 'DONE' | 'FAILED' | 'UNAVAILABLE';
}

export enum CaptionStyle {
  Basic = 'Basic',
  Outline = 'Outline',
  Background = 'Background'
}

export enum CaptionLineConfig {
  Default = 'Default',
  OneWord = '1 Word',
  OneLine = '1 Line',
  TwoLines = '2 Lines',
  ThreeLines = '3 Lines'
}

export interface AssetConfig {
  canvas_width: number;
  canvas_height: number;
  duration: number;
  background_color: string;
  background_image?: string;
  filename: string;
  videos: AssetConfigVideo[];
  captions: AssetConfigCaption[];
  isAnalysisAccurate?: boolean;
  remotion_captions?: boolean;
}

export interface AssetConfigVideo {
  start: number;
  end: number;
  x: number;
  y: number;
  z: number;
  bounding_width: number;
  bounding_height: number;
  radius?: number;
  meta: {
    source?: string;
    in_time: number;
    out_time?: number;
    excludes?: any[];
    crop_x: number;
    crop_y: number;
    crop_width: number;
    crop_height: number;
  };
}

interface AssetConfigCaption {
  style?: AssetConfigCaptionStyle;
  words?: AssetConfigCaptionWord[] | Word[];
  left_margin?: number;
  right_margin?: number;
  bottom_margin?: number;
  rotation?: number;
  z?: number;
  drift?: number;
}

export interface AssetConfigCaptionStyle {
  highlight_style?: AssetConfigCaptionStyleItem;
  regular_style?: AssetConfigCaptionStyleItem;
}

export interface AssetConfigCaptionStyleItem {
  background_color?: string;
  font?: string;
  font_color?: string;
  font_size?: number;
  font_location?: string;
  font_opacity?: string;
  scale_y?: number;
  scale_x?: number;
  border_style?: number;
  outline?: number;
  shadow?: number;
  bold?: number;
  alignment?: number;
}

interface AssetConfigCaptionWord {
  content: string;
  processed_start_time: number;
  processed_end_time: number;
  srt_index: number;
  hidden: true;
  speaker_label: string;
}

export enum ClipPublishedStatus {
  PUBLISHED = 'PUBLISHED',
  DRAFT = 'DRAFT',
  PROCESSING = 'PROCESSING',
  FAILED = 'FAILED'
}

export type ClipDeletes = Record<string, DeletedRange>;
export type ClipEdits = Record<string, EditedWord>;

export interface CompositeSegmentMetadata {
  edits: ClipEdits;
  merges: EditedWord[];
  excludes: Array<[number, number]>;
  captions: Array<Word>;
  fitMode: FitType;
  scenes: Scenes;
  duration: number;
  start: number;
  end: number;
  deletes: ClipDeletes;
  maxVideoDuration: number;
}

export interface VideoSegmentMetadata {
  start: number;
  end: number;
  deletes: ClipDeletes;
  maxVideoDuration: number;
  src: string;
}

export interface ClipMetadata {
  id: string;
  title: string;
  description: string;
  start: number;
  subtitle?: boolean;
  hide_borders?: boolean;
  captions: Array<Word>;
  /**
   * @deprecated Use player-config/remotion sources instead.
   * This field is being phased out and should not be used for new reads/writes.
   */
  config: AssetConfig | null;
  end: number;
  deletes?: ClipDeletes;
  excludes: Array<[number, number]>;
  edits: ClipEdits;
  merges: EditedWord[];
  layout: LayoutType;
  size: SizeType;
  fit: FitType;
  maxVideoDuration?: number;
  // intro and outro to clip
  intro?: string;
  introDuration?: number;
  outro?: string;
  outroDuration?: number;
  // magic layout applied to clip
  sourceId?: number;
  duration?: number;
  source?: SourceType;
  font_location?: string;
  caption_positions?: CaptionsPosition;
  caption_styles?: CaptionStyles;
  word_highlight_color?: string;
  highlight_type?: HighlightType;
  text_highlight_color?: string;
  is_full_length: boolean;
  publish_status?: ClipPublishedStatus | null;
  published_at?: string;
  magicLayout?: {
    textColor: string;
    backgroundColor: string;
    showSpeakerLabels: boolean;
    backgroundImage?: string;
  };
  visible_speakers?: SpeakerWithDetails[];
  visible_face_ids?: number[];
  template_id?: string;
  is_edited: boolean;
  updated_at?: string;
  created_at?: string;
  isLayoutProcessing?: boolean;
  isAnalysisAccurate?: boolean;
  isAutoActiveMappingOverridden?: boolean;
  isAutoActiveSpeakerIdentified?: boolean;
  remotionConfig: RemotionConfig | null;
  video_library_id: string;
  player_config: CompositionConfig | null;
  visible_video_ids?: string[];
  selected_lang?: string;
  original_lang: string;
  translated_langs: LanguageOption[];
  scenes?: Scenes;
  is_filler?: boolean;
  hidden?: boolean;
  speaker_label_styles: SpeakerLabelStyles;
  is_copied_for_ai_edits_testing?: boolean;
}

export type Clip = {
  id: string;
  event: string;
  broadcast: string;
  organization: string;
  title: string;
  content: MediaContentResponse;
  broadcast_data: BroadcastAsset;
  asset_metadata: ClipMetadata;
  isRemoved: boolean; // property for transition
  layout_status: {
    GRID: LayoutStatus;
    SPEAKER: LayoutStatus;
  };
  rating: AssetRating;
  updated_at: string;
  shared: boolean;
  locked?: boolean;
  thumbnail_details: ThumbnailDetails | null;
  first_frame_analysis: { [key: string]: any };
  saved_search: SavedSearches | null;
  translation_status: 'PROCESSING' | 'DONE' | 'FAILED';
  version: number | null;
  screenshare_detection_status?: ScreenshareDetectionStatus;
};

export const CAPTIONS_LINE_COUNT_MAP = {
  [CaptionLineConfig.OneLine]: 1,
  [CaptionLineConfig.TwoLines]: 2,
  [CaptionLineConfig.ThreeLines]: 3
} as const;
